import { Create, useSelect, useStepsForm } from "@refinedev/antd";
import {
  SwapOutlined,
  SelectOutlined,
  EditOutlined,
  AimOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  IResourceComponentsProps,
  useApiUrl,
  useCustomMutation,
  useList,
  useNavigation,
  useNotification,
  useOne,
} from "@refinedev/core";

import {
  IVessel,
  IShipco,
  IShipcoGroup,
  TransferType,
  IVesselTransferRequest,
} from "interfaces";
import { FormSection, FormFieldGrid } from "components/form";
import React, { useEffect, useState } from "react";
import { getStatusTagColor } from "constants/tagColors";

const { Text } = Typography;

const OLD_VESSELS_SHIPCO_NAME = "Deactivated Vessels";

const VESSEL_TYPE_OPTIONS = [
  { label: "Dry Cargo", value: "Dry Cargo" },
  { label: "Oil Tanker", value: "Oil Tanker" },
  { label: "Chemical Tanker", value: "Chemical Tanker" },
  { label: "Oil / Chemical Tanker", value: "Oil / Chemical Tanker" },
  { label: "Bulk Carrier", value: "Bulk Carrier" },
  { label: "General Cargo Vessel", value: "General Cargo Vessel" },
  { label: "Container Vessel", value: "Container Vessel" },
  { label: "Crude Carrier", value: "Crude Carrier" },
  { label: "Liquefied Gas Carrier", value: "Liquefied Gas Carrier" },
  { label: "Specialised Cargoes", value: "Specialised Cargoes" },
  { label: "Passenger Vessels", value: "Passenger Vessels" },
  { label: "Livestock Carriers", value: "Livestock Carriers" },
  { label: "Heavy-lift / Project Cargo", value: "Heavy-lift / Project Cargo" },
  { label: "Vessels", value: "Vessels" },
  { label: "Tugs", value: "Tugs" },
  { label: "Pipe-Layer", value: "Pipe-Layer" },
  { label: "Reefer Vessels", value: "Reefer Vessels" },
  { label: "Ro-Ro Vessels", value: "Ro-Ro Vessels" },
  { label: "Offshore Vessels", value: "Offshore Vessels" },
  { label: "Non IMO Vessels", value: "Non IMO Vessels" },
];

const VESSEL_EDIT_FIELDS = [
  "name", "type", "imoNumber", "callSign", "email",
  "holdStatus", "maxEmailSizeKb", "maxTransmissionSizeMb",
  "epsSystemId", "epsSystemIdBackup", "vesselLicenceId",
];

interface ValidateVesselEntry {
  vessel: {
    id: number;
    name: string;
    imoNumber: string;
    holdStatus: string;
    licence: {
      id: number;
      ukhoLicenceId: string;
      shipco: { id: number; registrationName: string };
      shipcoGroup: { id: number; registrationName: string } | null;
    };
  };
  shipco: { id: number; registrationName: string };
  shipcoGroup: { id: number; registrationName: string } | null;
  holdStatus: string;
}

interface ValidationResponse {
  targetShipco: IShipco;
  targetShipcoGroup: IShipcoGroup | null;
  oldVesselsShipco: IShipco | null;
  vessels: ValidateVesselEntry[];
}

export const VesselTransferCreate: React.FC<IResourceComponentsProps> = () => {
  const { list, show } = useNavigation();
  const { open } = useNotification();
  const apiUrl = useApiUrl();

  // Fetch "Deactivated Vessels" shipco ID from API
  const { result: oldVesselsShipcoResult } = useList<IShipco>({
    resource: "shipco",
    filters: [
      { field: "registrationName", operator: "eq", value: OLD_VESSELS_SHIPCO_NAME },
    ],
    pagination: { pageSize: 1 },
  });
  const oldVesselsShipcoId = oldVesselsShipcoResult?.data?.[0]?.id
    ? Number(oldVesselsShipcoResult.data[0].id)
    : null;

  const [transferType, setTransferType] = useState<TransferType | null>(null);
  const [selectedVessel, setSelectedVessel] = useState<string | null>(null);
  const [vesselId, setVesselId] = useState<number | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [targetShipcoId, setTargetShipcoId] = useState<number | null>(null);

  // Dynamic step indices based on transfer type
  const hasVesselEditStep = transferType === "between_shipcos" || transferType === "from_old_vessels";
  const STEP_TYPE = 0;
  const STEP_VESSEL = 1;
  const STEP_VESSEL_EDIT = hasVesselEditStep ? 2 : -1;
  const STEP_TARGET = hasVesselEditStep ? 3 : 2;
  const STEP_REVIEW = hasVesselEditStep ? 4 : 3;
  const TOTAL_STEPS = hasVesselEditStep ? 5 : 4;

  const {
    current,
    gotoStep,
    stepsProps,
    formProps,
    formLoading: isLoading,
  } = useStepsForm({
    resource: "vessel-transfer",
    redirect: false,
    submit: () => Promise.resolve(undefined as any),
  });

  // Vessel select — filter by deactivated shipco for reactivate, exclude for transfer
  const vesselFilters = (() => {
    if (transferType === "from_old_vessels" && oldVesselsShipcoId)
      return [{ field: "vesselShipco.id", operator: "eq" as const, value: oldVesselsShipcoId }];
    if (transferType === "between_shipcos" && oldVesselsShipcoId)
      return [{ field: "vesselShipco.id", operator: "ne" as const, value: oldVesselsShipcoId }];
    return [];
  })();
  const { selectProps: vesselSelectProps, query: vesselQuery } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "imoNumber",
    pagination: { mode: "server", pageSize: 100 },
    filters: vesselFilters,
    onSearch: (value) => [
      {
        operator: "or",
        value: [
          { field: "name", operator: "contains", value },
          { field: "imoNumber", operator: "contains", value },
        ],
      },
    ],
  });

  // Enrich vessel select — show "IMO - Name" in dropdown
  const enrichedVesselSelectProps = {
    ...vesselSelectProps,
    options: vesselSelectProps.options?.map((opt: any) => ({
      ...opt,
      label: `${opt.value} - ${opt.label}`,
    })),
  };

  // Fetch full vessel details for vessel edit step
  const { result: vesselDetail, query: vesselDetailQuery } = useOne<IVessel>({
    resource: "vessel",
    id: vesselId!,
    meta: { join: [{ field: "licence" }] },
    queryOptions: { enabled: !!vesselId },
  });

  // Populate vessel edit form fields when detail data arrives
  useEffect(() => {
    if (vesselDetail) {
      const v = vesselDetail;
      formProps.form?.setFieldsValue({
        name: v.name,
        type: v.type,
        imoNumber: v.imoNumber,
        callSign: v.callSign,
        email: v.email,
        holdStatus: v.holdStatus,
        maxEmailSizeKb: v.maxEmailSizeKb,
        maxTransmissionSizeMb: v.maxTransmissionSizeMb,
        epsSystemId: v.epsSystemId,
        epsSystemIdBackup: v.epsSystemIdBackup,
        vesselLicenceId: (v.licence as any)?.ukhoLicenceId || "N/A",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselDetail]);

  // Shipco select — exclude Deactivated Vessels for reactivate flow
  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "id",
    pagination: { mode: "server", pageSize: 100 },
    filters: (transferType === "from_old_vessels" || transferType === "between_shipcos") && oldVesselsShipcoId
      ? [{ field: "id", operator: "ne", value: oldVesselsShipcoId }]
      : [],
  });

  // Shipco group select — filter by target shipco
  const { selectProps: shipcoGroupSelectProps } = useSelect<IShipcoGroup>({
    resource: "shipco-group",
    optionLabel: "registrationName",
    optionValue: "id",
    pagination: { mode: "server", pageSize: 100 },
    filters: targetShipcoId
      ? [{ field: "shipco.id", operator: "eq", value: targetShipcoId }]
      : [],
    queryOptions: {
      enabled: !!targetShipcoId,
    },
  });

  // Validate mutation
  const { mutate: validateTransfer } = useCustomMutation();

  // Execute mutation
  const { mutate: executeTransfer, mutation: { isPending: isExecuting } } =
    useCustomMutation();

  // Update vessel mutation (for vessel edit step)
  const { mutate: updateVessel } = useCustomMutation();

  const handleValidate = () => {
    setIsValidating(true);
    setValidationResult(null);
    setValidationError(null);

    const payload = buildPayload();
    if (!payload) {
      setIsValidating(false);
      setValidationError("Please ensure transfer type, vessel, and target shipco are selected.");
      return;
    }

    validateTransfer(
      {
        url: `${apiUrl}/vessel-transfer/validate`,
        method: "post",
        values: payload,
        errorNotification: false,
      },
      {
        onSuccess: (data) => {
          setValidationResult(data?.data as ValidationResponse);
          setIsValidating(false);
        },
        onError: (error: any) => {
          const apiMessage = error?.response?.data?.message;
          setValidationError(apiMessage || error?.message || "Validation failed");
          setIsValidating(false);
        },
      }
    );
  };

  const executeTransferRequest = (payload: IVesselTransferRequest) => {
    executeTransfer(
      {
        url: `${apiUrl}/vessel-transfer`,
        method: "post",
        values: payload,
      },
      {
        onSuccess: (data) => {
          open?.({
            type: "success",
            message: "Transfer executed successfully",
          });
          const transferId = (data?.data as any)?.id;
          if (transferId) {
            show("vessel-transfer", transferId);
          } else {
            list("vessel-transfer");
          }
        },
        onError: (error: any) => {
          const apiMessage = error?.response?.data?.message;
          open?.({
            type: "error",
            message: "Transfer failed",
            description: apiMessage || error?.message || "An error occurred",
          });
        },
      }
    );
  };

  const handleExecute = () => {
    const payload = buildPayload();
    if (!payload) return;

    // For transfer/reactivation, update vessel first then execute transfer
    if (hasVesselEditStep && vesselId) {
      updateVessel(
        {
          url: `${apiUrl}/vessel/${vesselId}`,
          method: "patch",
          values: buildVesselPayload(),
        },
        {
          onSuccess: () => {
            executeTransferRequest(payload);
          },
          onError: (error: any) => {
            const apiMessage = error?.response?.data?.message;
            open?.({
              type: "error",
              message: "Failed to update vessel",
              description: apiMessage || error?.message || "An error occurred",
            });
          },
        }
      );
    } else {
      executeTransferRequest(payload);
    }
  };

  const buildPayload = (): IVesselTransferRequest | null => {
    const tt = formProps.form?.getFieldValue("transferType") || transferType;
    const vessel: string = formProps.form?.getFieldValue("vessel") || selectedVessel;
    const formTargetShipcoId = formProps.form?.getFieldValue("targetShipcoId") || targetShipcoId;

    if (!tt || !vessel) return null;

    const tShipcoId =
      tt === "to_old_vessels"
        ? oldVesselsShipcoId
        : formTargetShipcoId;

    if (!tShipcoId) return null;

    const reason = formProps.form?.getFieldValue("reason");
    const effectiveDate = formProps.form?.getFieldValue("effectiveDate");

    return {
      vessel_imo_numbers: [vessel],
      target_shipco_id: tShipcoId,
      transfer_type: tt,
      ...(reason && { reason }),
      ...(effectiveDate && {
        effective_date: effectiveDate.format("YYYY-MM-DD"),
      }),
    };
  };

  // Validate vessel edit fields and proceed to next step
  const handleValidateVesselAndProceed = async () => {
    try {
      await formProps.form?.validateFields([
        "name", "type", "email", "callSign",
        "holdStatus", "maxEmailSizeKb", "maxTransmissionSizeMb",
        "epsSystemId", "epsSystemIdBackup",
      ]);
    } catch {
      return;
    }
    gotoStep(STEP_TARGET);
  };

  const buildVesselPayload = () => ({
    name: formProps.form?.getFieldValue("name"),
    type: formProps.form?.getFieldValue("type"),
    callSign: formProps.form?.getFieldValue("callSign"),
    email: formProps.form?.getFieldValue("email"),
    holdStatus: formProps.form?.getFieldValue("holdStatus"),
    maxEmailSizeKb: formProps.form?.getFieldValue("maxEmailSizeKb"),
    maxTransmissionSizeMb: formProps.form?.getFieldValue("maxTransmissionSizeMb"),
    epsSystemId: formProps.form?.getFieldValue("epsSystemId"),
    epsSystemIdBackup: formProps.form?.getFieldValue("epsSystemIdBackup"),
  });

  // Handle vessel selection — look up vessel ID from query data
  const handleVesselSelect = (imoNumber: string) => {
    setSelectedVessel(imoNumber);
    const vessels = vesselQuery?.data?.data || [];
    const matched = vessels.find((v: IVessel) => v.imoNumber === imoNumber);
    if (matched) {
      setVesselId(Number(matched.id));
    }
  };

  // Reset downstream state when transfer type changes
  const handleTransferTypeChange = (type: TransferType) => {
    setTransferType(type);
    setSelectedVessel(null);
    setVesselId(null);
    setTargetShipcoId(null);
    setValidationResult(null);
    setValidationError(null);
    formProps.form?.resetFields([
      "vessel", "targetShipcoId", "shipcoGroupId", "reason", "effectiveDate",
      ...VESSEL_EDIT_FIELDS,
    ]);
    gotoStep(0);
  };

  // Dynamic step items
  const stepItems = hasVesselEditStep
    ? [
        { title: "Transfer Type" },
        { title: "Vessel & Details" },
        { title: "Edit Vessel" },
        { title: "Target Shipco" },
        { title: "Review & Confirm" },
      ]
    : [
        { title: "Transfer Type" },
        { title: "Vessel & Details" },
        { title: "Target Shipco" },
        { title: "Review & Confirm" },
      ];

  // Step content
  const formList = [
    // Step: Transfer Type
    <FormSection
      key="step-type"
      title="Transfer Type"
      subtitle="Select the type of vessel transfer"
      icon={<SwapOutlined />}
      variant="elevated"
    >
      <FormFieldGrid columns={1}>
        <Form.Item
          name="transferType"
          rules={[{ required: true, message: "Please select a transfer type" }]}
        >
          <Radio.Group
            onChange={(e) => handleTransferTypeChange(e.target.value)}
            value={transferType}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Radio value="to_old_vessels" style={{ fontSize: 15 }}>
                <Text strong>Deactivate</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Move vessels to deactivated vessels
                </Text>
              </Radio>
              <Radio value="between_shipcos" style={{ fontSize: 15 }}>
                <Text strong>Transfer between Shipcos</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Transfer vessels from one shipco to another
                </Text>
              </Radio>
              <Radio value="from_old_vessels" style={{ fontSize: 15 }}>
                <Text strong>Reactivate</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Move vessels from deactivated vessels to an active shipco
                </Text>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </FormFieldGrid>
    </FormSection>,

    // Step: Vessel Selection + Details
    <FormSection
      key="step-vessel"
      title="Vessel Selection"
      subtitle="Select a vessel for transfer"
      icon={<SelectOutlined />}
      variant="elevated"
    >
      <FormFieldGrid columns={1}>
        <Form.Item
          label="Vessel"
          name="vessel"
          rules={[{ required: true, message: "Select a vessel" }]}
        >
          <Select
            options={enrichedVesselSelectProps.options}
            onSearch={enrichedVesselSelectProps.onSearch}
            loading={enrichedVesselSelectProps.loading}
            placeholder="Search by IMO or vessel name"
            onChange={(value: string) => handleVesselSelect(value)}
            value={selectedVessel}
            showSearch
            filterOption={false}
          />
        </Form.Item>
      </FormFieldGrid>

      <div style={{ marginTop: 16 }}>
        <FormFieldGrid columns={2}>
          <Form.Item label="Reason (optional)" name="reason">
            <Input.TextArea rows={3} placeholder="Reason for transfer" />
          </Form.Item>
          <Form.Item
            label="Effective Date"
            name="effectiveDate"
            rules={[{ required: true, message: "Effective date is required" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </FormFieldGrid>
      </div>
    </FormSection>,

    // Step: Vessel Edit (only for transfer/reactivation)
    ...(hasVesselEditStep
      ? [
          <FormSection
            key="step-vessel-edit"
            title="Edit Vessel"
            subtitle="Update vessel details before transfer"
            icon={<EditOutlined />}
            variant="elevated"
          >
            {vesselDetailQuery.isLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">Loading vessel details...</Text>
                </div>
              </div>
            ) : (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <FormFieldGrid columns={2}>
                  <Form.Item
                    label="Vessel Name"
                    name="name"
                    rules={[{ required: true, message: "Vessel name is required" }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label="Vessel Type"
                    name="type"
                    rules={[{ required: true, message: "Vessel type is required" }]}
                  >
                    <Select options={VESSEL_TYPE_OPTIONS} />
                  </Form.Item>
                  <Form.Item label="IMO Number" name="imoNumber">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="Call Sign" name="callSign">
                    <Input />
                  </Form.Item>
                </FormFieldGrid>
                <FormFieldGrid columns={2}>
                  <Form.Item label="UKHO Licence" name="vesselLicenceId">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="Hold Status" name="holdStatus">
                    <Select
                      options={[
                        { label: "On Hold", value: "on hold" },
                        { label: "Not On Hold", value: "not on hold" },
                      ]}
                    />
                  </Form.Item>
                </FormFieldGrid>
                <FormFieldGrid columns={1}>
                  <Form.Item
                    label="Communication Email"
                    name="email"
                    rules={[{ required: true, message: "Email is required" }]}
                  >
                    <Input />
                  </Form.Item>
                </FormFieldGrid>
                <FormFieldGrid columns={2}>
                  <Form.Item label="Max Email Size (Bytes)" name="maxEmailSizeKb">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Max Transmission Size (MB)" name="maxTransmissionSizeMb">
                    <Input />
                  </Form.Item>
                </FormFieldGrid>
                <FormFieldGrid columns={2}>
                  <Form.Item label="EPS System ID (Primary)" name="epsSystemId">
                    <Input />
                  </Form.Item>
                  <Form.Item label="EPS System ID (Backup)" name="epsSystemIdBackup">
                    <Input />
                  </Form.Item>
                </FormFieldGrid>
              </Space>
            )}
          </FormSection>,
        ]
      : []),

    // Step: Target Shipco
    <FormSection
      key="step-target"
      title="Target Shipco"
      subtitle="Select destination shipco for the vessel"
      icon={<AimOutlined />}
      variant="elevated"
    >
      <FormFieldGrid columns={1}>
        {transferType === "to_old_vessels" ? (
          <Form.Item label="Target Shipco">
            <Input disabled value="Deactivated Vessels" />
          </Form.Item>
        ) : (
          <>
            <Form.Item
              label="Target Shipco"
              name="targetShipcoId"
              rules={[{ required: true, message: "Select a target shipco" }]}
            >
              <Select
                options={shipcoSelectProps.options}
                onSearch={shipcoSelectProps.onSearch}
                loading={shipcoSelectProps.loading}
                placeholder="Select target shipco"
                onChange={(value: number) => {
                  setTargetShipcoId(value);
                  formProps.form?.setFieldValue("shipcoGroupId", undefined);
                }}
                showSearch
                filterOption={false}
              />
            </Form.Item>
            <Form.Item label="Shipco Group (optional)" name="shipcoGroupId">
              <Select
                options={shipcoGroupSelectProps.options}
                onSearch={shipcoGroupSelectProps.onSearch}
                loading={shipcoGroupSelectProps.loading}
                placeholder={
                  targetShipcoId
                    ? "Select shipco group"
                    : "Select a shipco first"
                }
                disabled={!targetShipcoId}
                allowClear
                showSearch
                filterOption={false}
              />
            </Form.Item>
          </>
        )}
      </FormFieldGrid>
    </FormSection>,

    // Step: Review & Confirm
    <FormSection
      key="step-review"
      title="Review & Confirm"
      subtitle="Validate and execute the transfer"
      icon={<CheckCircleOutlined />}
      variant="elevated"
    >
      {isValidating ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Validating transfer...</Text>
          </div>
        </div>
      ) : validationError ? (
        <Alert
          type="error"
          message="Validation Failed"
          description={validationError}
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={handleValidate}>
              Retry
            </Button>
          }
        />
      ) : validationResult ? (
        <>
          <Alert
            type="success"
            message="Validation Passed"
            description={`${validationResult.vessels.length} vessel(s) ready for transfer to ${validationResult.targetShipco?.registrationName || "target shipco"}.`}
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            dataSource={validationResult.vessels}
            rowKey={(record) => record.vessel.imoNumber}
            size="small"
            pagination={false}
          >
            <Table.Column
              title="IMO"
              width={120}
              render={(_, record: ValidateVesselEntry) => record.vessel.imoNumber}
            />
            <Table.Column
              title="Vessel Name"
              width={180}
              render={(_, record: ValidateVesselEntry) => record.vessel.name}
            />
            <Table.Column
              title="Current Shipco"
              width={220}
              render={(_, record: ValidateVesselEntry) =>
                record.shipco?.registrationName || "N/A"
              }
            />
            <Table.Column
              title="Hold Status"
              width={130}
              render={(_, record: ValidateVesselEntry) =>
                record.holdStatus ? (
                  <Tag color={getStatusTagColor(record.holdStatus)}>
                    {record.holdStatus}
                  </Tag>
                ) : (
                  <Text>N/A</Text>
                )
              }
            />
          </Table>
          <div style={{ marginTop: 24, textAlign: "right" }}>
            <Button
              type="primary"
              size="large"
              onClick={handleExecute}
              loading={isExecuting}
            >
              Execute Transfer
            </Button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text type="secondary">
            Click "Validate" to check the transfer before executing.
          </Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleValidate}>
              Validate Transfer
            </Button>
          </div>
        </div>
      )}
    </FormSection>,
  ];

  const canGoNext = () => {
    if (current === STEP_TYPE) return !!transferType;
    if (current === STEP_VESSEL) return !!selectedVessel;
    if (current === STEP_VESSEL_EDIT) return true; // validation on click
    if (current === STEP_TARGET) {
      return transferType === "to_old_vessels" || !!targetShipcoId;
    }
    return false;
  };

  const handleNextClick = async () => {
    // Vessel edit step — validate fields and proceed
    if (current === STEP_VESSEL_EDIT) {
      handleValidateVesselAndProceed();
      return;
    }

    // Validate effective date before proceeding from vessel step
    if (current === STEP_VESSEL) {
      try {
        await formProps.form?.validateFields(["effectiveDate"]);
      } catch {
        return;
      }
    }

    // Auto-set target for deactivation on target step
    if (current === STEP_TARGET) {
      if (transferType === "to_old_vessels" && oldVesselsShipcoId) {
        setTargetShipcoId(oldVesselsShipcoId);
      }
    }

    const nextStep = current + 1;

    // Entering review step — auto-validate
    if (nextStep === STEP_REVIEW) {
      // Refetch vessel data so review reflects any vessel edit changes
      if (hasVesselEditStep && vesselId) {
        vesselDetailQuery.refetch();
      }
      setIsValidating(true);
      setValidationResult(null);
      setValidationError(null);
    }

    gotoStep(nextStep);

    if (nextStep === STEP_REVIEW) {
      setTimeout(handleValidate, 300);
    }
  };

  return (
    <Create
      breadcrumb={false}
      headerProps={{
        onBack: () => list("vessel-transfer"),
        title: "New Vessel Transfer",
        subTitle: "transfer vessels between shipcos",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">ready</Tag>
        ),
        extra: <></>,
      }}
      saveButtonProps={{ style: { display: "none" } }}
    >
      <Steps
        {...stepsProps}
        current={current}
        items={stepItems}
      />
      <div>
        <Form {...formProps} layout="vertical" style={{ paddingTop: 20 }}>
          <Space direction="vertical" size={24} style={{ width: "100%" }}>
            {formList[current]}
          </Space>
        </Form>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: 20,
          }}
        >
          <div>
            {current > 0 && (
              <Button
                onClick={() => {
                  if (current === STEP_REVIEW) {
                    setValidationResult(null);
                    setValidationError(null);
                  }
                  gotoStep(current - 1);
                }}
              >
                Previous
              </Button>
            )}
          </div>
          <div>
            {current < TOTAL_STEPS - 1 && (
              <Button
                type="primary"
                disabled={!canGoNext()}
                onClick={handleNextClick}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </Create>
  );
};
